import { Component, EventEmitter, OnDestroy, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap'
import { ascending } from 'd3-array'
import { sortBy } from 'lodash-es'
import { combineLatest, interval, Observable, Subscription } from 'rxjs'
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { AppLocaleScopeTimezoneDatePipe } from '../../../shared/pipes/app-locale-scope-timezone-date/app-locale-scope-timezone-date.pipe'
import { Scope } from '../../models'
import { AppConfigService } from '../../services/app-config.service'
import { AuthService } from '../../services/auth.service'
import { ExportsService } from '../../services/exports.service'
import { FeatureFlagsService } from '../../services/feature-flag.service'
import { I18nConfigService } from '../../services/i18n-config.service'
import { AppPage, NavStore } from '../../services/nav-store.service'
import { isQueryReady, ScopeService } from '../../services/scope.service'

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnDestroy {

  selectedScope: Scope.T | null;
  scopesError = false;
  scopesCarrousel: (Scope.T & { displayName: string })[];

  version = environment.version
  currentTime$: Observable<string | null> =interval(1000).pipe(
    map( _interval => this.appLocaleScopeTimezoneDate.transform(new Date(), 'shortTime')),
    distinctUntilChanged(),
    shareReplay(1)
  )

  clientLogo$ = combineLatest([ this.appConfigService.state$, this.scopeService.scope$ ]).pipe(
    map(([ appConfig, scope ]) => ( scope !== null )
      ? appConfig.theme?.logo
      : null),
    distinctUntilChanged()
  )
  readingsFeatureFlag$ = this.featureFlagsService.readings$
  reportsFeatureFlag$ = this.featureFlagsService.reports$
  watchesFeatureFlag$ = this.featureFlagsService.watches$
  wasteFeatureFlag$ = this.featureFlagsService.waste$

  currentLocale$ = this.i18nConfigService.selectedLanguage$
  availableLanguages$ = this.i18nConfigService.availableLanguages$

  hrefToProfile$ = this.router.events.pipe(
    filter((routerEventOrInit): routerEventOrInit is NavigationEnd =>routerEventOrInit instanceof NavigationEnd),
    map(({ urlAfterRedirects })=>urlAfterRedirects),
    startWith(this.router.routerState.snapshot.url),
    map((urlOnRecap)=>`/profile?redirect=recap${urlOnRecap}`),
    distinctUntilChanged(),
    shareReplay(1),
  )

  scopes$ = this.scopeService.scopesListQuery$.pipe(
    distinctUntilChanged(),
    map((serverQuery)=> isQueryReady(serverQuery)
      ? serverQuery.data
        .filter(Scope.isViewAllowed)
        .map(scope => ({ ...scope, displayName: Scope.displayNameOf(scope) }))
      : []),
    shareReplay(1)
  )

  currentScopeDisplayName$ =  this.scopeService.scope$.pipe(
    distinctUntilChanged(),
    map((scope)=> scope ? Scope.displayNameOf(scope) : ''),
    shareReplay(1)
  )

  exports$ = combineLatest([ this.exportsService.list$, this.currentLocale$ ]).pipe(
    map(([ exports, currentLocale ]) => exports.map(({ name, href }) => ({ name: name.translations[currentLocale] ?? name.default, href })))
  )

  helpUrl$ = this.appConfigService.state$.pipe(
    map(({ helpUrl }) => helpUrl !== undefined
      ? helpUrl
      : null)
  )

  @Output()
  onMobileSidenavToggle = new EventEmitter();

  isScopeDropdownOpen = false
  @ViewChild('scopeDropdown', { 'static': false }) scopeDropdown: NgbDropdown

  hoveredLink$ = this.navStore.hoveredLink$

  scopeFilterValue = ''

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  scopeFilterFn = (_: {displayName: string}): boolean => true

  scopeSortFn = (a: Scope.T, b: Scope.T): number => ascending(a.code, b.code)

  showProfileLink$ = this.featureFlagsService.newAuthFrontend$.pipe(
    map(({ isEnabled })=>!isEnabled)
  )

  // subscriptions
  subscriptions: Subscription[] = [
    this.scopeService.scope$.subscribe((scope)=>{
      this.selectedScope = scope
      this.isScopeDropdownOpen = false
      this.scopeDropdown?.close()
    }),
    this.scopes$.subscribe((scopes)=>{
      this.scopesCarrousel = sortBy(scopes.filter(scope => Scope.isViewAllowed(scope)), scope => scope.code)
    })
  ]

  constructor (
    private auth: AuthService,
    private router: Router,
    private scopeService: ScopeService,
    private navStore: NavStore,
    private i18nConfigService: I18nConfigService,
    private appConfigService: AppConfigService,
    private featureFlagsService: FeatureFlagsService,
    private appLocaleScopeTimezoneDate: AppLocaleScopeTimezoneDatePipe,
    private exportsService: ExportsService,
  ) {}

  ngOnDestroy (): void {
    this.subscriptions.forEach(subscription=>subscription.unsubscribe())
  }

  selectLanguage (language: string): void {
    this.i18nConfigService.selectLanguage(language)
  }
  previousScope (): void {
    const selectedScope = this.selectedScope
    if(selectedScope === null) throw 'no selected scope'
    const index = this.scopesCarrousel.findIndex(scope => scope.id === selectedScope.id)
    if( index <= 0 || index > this.scopesCarrousel.length - 1) {
      return this.switchScope(this.scopesCarrousel[this.scopesCarrousel.length - 1])
    }
    return this.switchScope(this.scopesCarrousel[index - 1])
  }
  nextScope (): void {
    const selectedScope = this.selectedScope
    if(selectedScope === null) throw 'no selected scope'
    const index = this.scopesCarrousel.findIndex(scope => scope.id === selectedScope.id)
    if( index < 0 || index >= this.scopesCarrousel.length - 1) {
      return this.switchScope(this.scopesCarrousel[0])
    }
    return this.switchScope(this.scopesCarrousel[index + 1])
  }

  logout (): void {
    this.auth.logout()
  }

  hoverNavIcon (page: AppPage): void {
    this.navStore.highlightFeature(page)
  }

  onScopeDropdownToggle (isOpen: boolean): void {
    if((!isOpen) || (this.selectedScope === null)) return
    this.updateFilter('')
    const scopeLink = document.getElementById(`link-${this.selectedScope.id}`)
    const scopesList = document.getElementById('scope-list-container')
    if(!scopeLink || !scopesList) return

    // FIXME: Very ugly but needed by chromium based browsers to make scroll and then focus. Without the 2 timeout they overrides each overs
    setTimeout(() => {
      scopesList.focus()
      scopeLink?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' })
      setTimeout(() => {
        document.getElementById('searchScopeInputHeader')?.focus()
      }, 500)
    })
  }
  updateFilter (value: string): void {
    this.scopeFilterValue = value
    this.scopeFilterFn = scope => scope.displayName.toLowerCase().includes(value.toLowerCase())
  }
  switchScope (scope: Scope.T): void {
    if(!Scope.isViewAllowed(scope)){
      throw Error('this operation requires user to have View permission on the selected scope')
    }
    this.scopeService.switchScope(scope.id)
  }
  /**
   * On submit (pressing enter on the filter input), we navigate to the scope if there is only one left after filtering
   */
  submit (): void {
    const filteredScopes = this.scopesCarrousel
      .filter(this.scopeFilterFn)
    if (filteredScopes.length === 1) {
      this.switchScope(filteredScopes[0])
      // this.router.navigate([ `/${filteredScopes[0].id}`, 'charts' ]) // TODO add scope data in state
    }
  }
}
